import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TextField,
  IconButton,
  Typography,
  Paper,
  Collapse,
  Box,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  IconTrash,
  IconDeviceFloppy,
  IconEraser,
  IconEdit,
  IconX,
} from "@tabler/icons-react";
import { supabase } from "../../../supabaseClient";
import Fuse from "fuse.js";

interface ProductCalculatorProps {
  id: string;
  name: string;
  price: string;
  onNameChange: (id: string, value: string) => void;
  onAmountChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}

const calculateTotal = (value: string): number => {
  const numValue = parseFloat(value) || 0;
  return Math.ceil(numValue * 6.96);
};

export const ProductCalculator = React.memo(
  ({
    id,
    name: propName,
    price: propAmount,
    onNameChange,
    onAmountChange,
    onRemove,
    canRemove,
  }: ProductCalculatorProps) => {
    const [name, setName] = useState(propName);
    const [price, setAmount] = useState(propAmount);
    const [showDetails, setShowDetails] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const fuse = React.useMemo(
      () => new Fuse(products, { keys: ["name"], threshold: 0.4 }),
      [products]
    );

    // Sincronizar con props externos
    useEffect(() => {
      setName(propName);
    }, [propName]);

    useEffect(() => {
      setAmount(propAmount);
    }, [propAmount]);

    // Cargar productos al montar
    useEffect(() => {
      const fetchProducts = async () => {
        setLoading(true);
        const { data, error } = await supabase.from("yanbal").select("*");
        if (!error && data) setProducts(data);
        setLoading(false);
      };
      fetchProducts();
    }, []);

    // Buscar productos por name (fix: usar name si search vacío)
    useEffect(() => {
      // Si acabo de seleccionar un producto, no muestro resultados
      if (
        selectedProduct &&
        (search === "" || search === selectedProduct.name)
      ) {
        setSearchResults([]);
        return;
      }
      const term = (search ?? "").trim() || (name ?? "").trim();
      if (term === "") {
        setSearchResults([]);
        return;
      }
      const results = fuse.search(term).map((r) => r.item);
      setSearchResults(results);
    }, [search, name, fuse, selectedProduct]);

    const handleAmountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value) || value === "") {
          setAmount(value);
          onAmountChange(id, value);
        }
      },
      [id, onAmountChange]
    );

    const handleRemove = useCallback(() => {
      onRemove(id);
    }, [id, onRemove]);

    // Seleccionar producto de la búsqueda
    const handleSelectProduct = (product: any) => {
      setSelectedProduct(product);
      setName(product.name);
      setAmount(product.price.toString());
      onNameChange(id, product.name);
      onAmountChange(id, product.price.toString());
      setSearch("");
      setSearchResults([]); // Oculta la lista de resultados inmediatamente
    };

    // Guardar/actualizar producto en Supabase
    const handleSaveProduct = async () => {
      setLoading(true);
      if (selectedProduct) {
        // Update
        await supabase
          .from("yanbal")
          .update({ name: name, price: parseFloat(price) })
          .eq("id", selectedProduct.id);
      } else {
        // Insert
        await supabase
          .from("yanbal")
          .insert([{ name: name, price: parseFloat(price) }]);
      }
      // Refrescar lista
      const { data } = await supabase.from("yanbal").select("*");
      setProducts(data || []);
      setLoading(false);
    };

    // Eliminar producto
    const handleDeleteProduct = async () => {
      if (!selectedProduct) return;
      setLoading(true);
      await supabase.from("yanbal").delete().eq("id", selectedProduct.id);
      setSelectedProduct(null);
      setName("");
      setAmount("");
      const { data } = await supabase.from("yanbal").select("*");
      setProducts(data || []);
      setLoading(false);
      setOpenDeleteModal(false);
    };

    // Determinar si el producto es nuevo o editando uno existente
    const isEditing = !!selectedProduct;
    const isNameChanged = isEditing && name !== selectedProduct?.name;
    const isAmountChanged =
      isEditing && price !== selectedProduct?.price?.toString();
    const isNewProduct = !selectedProduct && (name ?? "").trim().length > 0;
    const canSave =
      ((isEditing && (isNameChanged || isAmountChanged)) || isNewProduct) &&
      (name ?? "").trim().length > 0 &&
      (price ?? "").trim().length > 0 &&
      !loading;

    const total = calculateTotal(price);
    const inversion = Math.ceil(total * 0.25);
    const ofertaMaxima = Math.ceil(total * 0.15);
    const ofertaMinima = Math.ceil(total * 0.1);

    return (
      <Paper
        elevation={3}
        sx={{ p: 2, mb: 2, borderRadius: 2, height: "100%" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0, mb: 2 }}>
          <Box sx={{ display: "flex", flexGrow: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Nombre del Producto"
              value={search || name}
              inputRef={inputRef}
              onChange={(e) => {
                setSearch(e.target.value);
                setName(e.target.value);
                onNameChange(id, e.target.value);
                if (
                  !selectedProduct ||
                  selectedProduct.name !== e.target.value
                ) {
                  setSelectedProduct(null);
                }
              }}
              placeholder="Buscar o ingresar name"
              sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              autoComplete="off"
              InputProps={{
                sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                endAdornment:
                  (search || name) && (search || name).length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setName("");
                        setAmount("");
                        onNameChange(id, "");
                        setSelectedProduct(null);
                        setTimeout(() => {
                          inputRef.current?.focus();
                        }, 0);
                      }}
                      sx={{ visibility: search || name ? "visible" : "hidden" }}
                    >
                      <IconEraser size={20} />
                    </IconButton>
                  ) : null,
              }}
            />
            <IconButton
              onClick={handleSaveProduct}
              disabled={!canSave}
              color="primary"
              size="medium"
              sx={{
                borderRadius: 0,
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: canSave
                  ? "rgba(76, 175, 80, 0.18)"
                  : "rgba(76, 175, 80, 0.06)",
                color: canSave ? "primary.main" : "grey.400",
                height: "56px",
                width: "48px",
                ml: "-1px",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  bgcolor: canSave
                    ? "rgba(76, 175, 80, 0.32)"
                    : "rgba(76, 175, 80, 0.06)",
                  color: canSave ? "white" : "grey.400",
                },
              }}
            >
              {isEditing ? (
                <IconEdit
                  size={20}
                  color={canSave ? "#4caf50" : "#bdbdbd"}
                />
              ) : (
                <IconDeviceFloppy
                  size={20}
                  color={canSave ? "#4caf50" : "#bdbdbd"}
                />
              )}
            </IconButton>
            <IconButton
              onClick={() => setOpenDeleteModal(true)}
              disabled={loading || !selectedProduct}
              color="error"
              size="medium"
              sx={{
                borderRadius: 0,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                border: "1px solid",
                borderColor: "error.main",
                bgcolor:
                  !loading && selectedProduct
                    ? "rgba(244, 67, 54, 0.18)"
                    : "rgba(244, 67, 54, 0.06)",
                color: !loading && selectedProduct ? "error.main" : "grey.400",
                height: "56px",
                width: "48px",
                ml: "-1px",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  bgcolor:
                    !loading && selectedProduct
                      ? "rgba(244, 67, 54, 0.32)"
                      : "rgba(244, 67, 54, 0.06)",
                  color: !loading && selectedProduct ? "white" : "grey.400",
                },
              }}
            >
              <IconTrash
                size={20}
                color={!loading && selectedProduct ? "#f44336" : "#bdbdbd"}
              />
            </IconButton>
          </Box>
          {canRemove && (
            <IconButton
              onClick={handleRemove}
              size="small"
              sx={{
                color: "error.main",
                borderRadius: "50%",
                bgcolor: "white",
                border: "1px solid",
                borderColor: "error.main",
                p: 0.7,
                ml: 1,
                "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.08)" },
                alignSelf: "flex-end",
                mb: 1,
              }}
            >
              <IconX size={20} />
            </IconButton>
          )}
        </Box>
        {/* Resultados de búsqueda */}
        {searchResults.length > 0 && (
          <Paper sx={{ maxHeight: 200, overflow: "auto", mb: 1 }}>
            {searchResults.map((product) => (
              <Box
                key={product.id}
                sx={{
                  p: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#eee" },
                }}
                onClick={() => handleSelectProduct(product)}
              >
                <Typography>
                  {product.name} <b>({product.price} $)</b>
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
        <TextField
          fullWidth
          variant="outlined"
          label="Monto en $ (USD)"
          value={price}
          onChange={handleAmountChange}
          placeholder="Ingrese el monto"
          type="number"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            sx: { fontSize: "1.25rem", py: 1 },
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  variant="h6"
                  color="text.secondary"
                >
                  $
                </Typography>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Dividir entre 2"
                  onClick={() => {
                    const num = parseFloat(price);
                    if (!isNaN(num) && num !== 0) {
                      const divided = (num / 2).toFixed(1);
                      setAmount(divided);
                      onAmountChange(id, divided);
                    }
                  }}
                  disabled={
                    !price ||
                    isNaN(parseFloat(price)) ||
                    parseFloat(price) === 0
                  }
                  size="large"
                  sx={{ ml: 1 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                  >
                    ½
                  </Typography>
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            textAlign: "center",
            userSelect: "none",
            cursor: "pointer",
            "&:hover .operation": {
              color: "primary.main",
            },
          }}
          onClick={() => setShowDetails(!showDetails)}
        >
          <Typography
            variant="h6"
            className="operation"
            sx={{
              color: "text.secondary",
              mb: 1,
              transition: "color 0.2s",
            }}
          >
            {price || "0"} × 6.96
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            = {total.toLocaleString("es-BO")} Bs.
          </Typography>
        </Box>

        <Collapse in={showDetails}>
          <Box
            component={Paper}
            variant="outlined"
            sx={{ p: 2, borderRadius: 1 }}
          >
            {/* 10% Oferta Mínima */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "medium" }}
              >
                Oferta Mín. (10%):
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography>
                  {ofertaMinima.toLocaleString("es-BO")} Bs.
                </Typography>
                <Typography color="text.secondary">|</Typography>
                <Typography>
                  {(total - ofertaMinima).toLocaleString("es-BO")} Bs.
                </Typography>
              </Box>
            </Box>

            {/* 15% Oferta Máxima */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "medium" }}
              >
                Oferta Máx. (15%):
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography>
                  {ofertaMaxima.toLocaleString("es-BO")} Bs.
                </Typography>
                <Typography color="text.secondary">|</Typography>
                <Typography>
                  {(total - ofertaMaxima).toLocaleString("es-BO")} Bs.
                </Typography>
              </Box>
            </Box>

            {/* 25% Inversión (en negrita) */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: "bold",
                backgroundColor: "rgba(0,0,0,0.04)",
                p: 1,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold" }}
              >
                Inversión (25%):
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>
                  {inversion.toLocaleString("es-BO")} Bs.
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: "bold" }}
                >
                  |
                </Typography>
                <Typography sx={{ fontWeight: "bold" }}>
                  {(total - inversion).toLocaleString("es-BO")} Bs.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>

        {/* Modal de confirmación para eliminar */}
        <Dialog
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
        >
          <DialogTitle>¿Eliminar producto?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Estás seguro de que deseas eliminar este producto? Esta acción no
              se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteModal(false)}
              color="primary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteProduct}
              color="error"
              variant="contained"
              autoFocus
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  }
);

ProductCalculator.displayName = "ProductCalculator";
